import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { useSSRStorePrefill } from "../../../store/reducers";
import { type INavProps, narrowNavProps } from "../ProductGrid";
import { StreamFieldProductGrid } from "../StreamFieldProductGrid";
import { GridSortPlacement } from "../models";
import { prefillSSRProductGridData } from "../ssr";
import { BuildGridTile } from "../tiles";

/**
 * `hideNav` and `header` arrive as one unit -- see `INavProps`. They are props
 * rather than snippet fields for the reason `sortPlacement` is: whether the
 * embedding page draws its own filter chrome is a property of that page, and
 * the same snippet backs a plain `ProductGridBlock` and 1-800's
 * `ProductVariantGridBlock`.
 */
type IProps = Immutable<templates.ProductGridBlockTemplate> &
    INavProps & {
        /**
         * Substitutes the tile rendered per product. See `BuildGridTile`.
         *
         * A prop rather than a `products_data` variation because which tile to
         * render is a property of the page the grid is embedded in, not of the
         * grid snippet: the same snippet backs a plain `ProductGridBlock` and
         * 1-800's `ProductVariantGridBlock`.
         *
         * Setting it narrows where the snippet's `disable_hover_overlay` applies:
         * that field configures `ProductCard`, which a custom tile replaces for
         * the products it draws itself and leaves in place for any it hands back
         * through `renderDefault`.
         */
        buildTile?: BuildGridTile;

        /**
         * Moves the sort control into the sidebar. See `GridSortPlacement`.
         *
         * A prop rather than a snippet field for the same reason `buildTile` is:
         * where the control sits is a property of the page the grid is embedded
         * in, and the same snippet backs a plain `ProductGridBlock` and 1-800's
         * `ProductVariantGridBlock`.
         */
        sortPlacement?: GridSortPlacement;
    };

export const ProductGridSSR = (props: IProps): React.JSX.Element => {
    useSSRStorePrefill(
        {
            products_data: props.products_data,
            initial_filter_state: props.initial_filter_state,
            initial_sort_method: props.initial_sort_method,
        },
        prefillSSRProductGridData,
    );

    return (
        <StreamFieldProductGrid
            gridVariant="streamfield"
            productCategoryID={String(props.category_id)}
            filters={props.filters_data}
            sorters={props.sorters_data}
            hideSort={props.hide_sort ? "true" : "false"}
            disableHoverOverlay={props.disable_hover_overlay ? "true" : "false"}
            buildTile={props.buildTile}
            sortPlacement={props.sortPlacement}
            {...narrowNavProps(props)}
        />
    );
};
